import { useRouter } from 'next/router'
import React from 'react'
import { ROUTE } from 'shared-constants'
import { type SpaceProps } from 'styled-system'
import { BUTTON_SIZE } from 'components/Button'
import { useClientFavoriteExpertsManagement } from 'components/ExpertCard'
import { Flex } from 'components/Layout'
import { StyledLink } from 'components/Link'
import { GA4_SIGN_IN_AREAS } from 'providers/googleAnalytics'
import { useUserAuth } from 'providers/userAuth'
import { SPACE } from 'Theme'
import { type Attachment } from 'types/graphql-generated'
import { useMedia } from 'useMedia'
import { formatLocation } from 'utils/formatters'
import { silentPushUrl } from 'utils/pushUrl'
import { ExpertCarousel } from './ExpertCarousel'
import { ExpertInfo } from './ExpertInfo'
import {
  EXPERT_BOOK_CONSULTATION_ID,
  EXPERT_IMAGE_MIN_NUMBER_TO_SHOW_CAROUSEL,
} from './utils'

interface HeaderProps extends SpaceProps {
  id: number
  slug: string
  route: string
  images: Pick<Attachment, 'url' | 'alt'>[]
  hasSessions: boolean
  headshotImageAttachment: Pick<Attachment, 'url' | 'alt'>
  profileName: string
  shortDescription: string
  instagramAccountUrl: string
  primaryLocation: string
  city: string
  countryIsoCode: string
}

export const Header: React.FC<HeaderProps> = ({
  id,
  slug,
  route,
  images,
  hasSessions,
  headshotImageAttachment,
  profileName,
  shortDescription,
  instagramAccountUrl,
  primaryLocation,
  city,
  countryIsoCode,
  ...rest
}) => {
  const router = useRouter()
  const { isClientSignedIn, user } = useUserAuth()
  const {
    addExpertToFavorites,
    removeExpertFromFavorites,
    clientFavoriteExpertsSlugs,
    isLoading: isFavoriteExpertLoading,
  } = useClientFavoriteExpertsManagement({
    clientId: isClientSignedIn && user ? user.id : null,
  })
  const { MOBILE: isMobile } = useMedia()

  const hasExpertCarousel =
    images.length >= EXPERT_IMAGE_MIN_NUMBER_TO_SHOW_CAROUSEL

  return (
    <Flex
      flexDirection={{ MOBILE: 'column', TABLET: 'row' }}
      width="100%"
      mb={SPACE.PX_40}
      {...rest}
    >
      <ExpertInfo
        isMobile={isMobile && hasExpertCarousel}
        imageAttachment={headshotImageAttachment}
        name={profileName}
        description={shortDescription}
        socialLinks={{
          instagram: instagramAccountUrl,
        }}
        location={formatLocation({
          primaryLocation,
          city,
          countryIsoCode,
        })}
        isFavorite={
          isClientSignedIn && clientFavoriteExpertsSlugs.includes(slug)
        }
        isAddToFavoriteLoading={isFavoriteExpertLoading}
        onRemoveFromFavorite={() =>
          removeExpertFromFavorites({
            expertId: id,
          })
        }
        onAddToFavorite={() =>
          addExpertToFavorites({
            expertId: id,
          })
        }
        onNotAuthorizedClick={() => {
          silentPushUrl({
            router,
            route,
            params: {
              slug,
              isAuthModalOpen: true,
              expertId: id,
              fromUrl: GA4_SIGN_IN_AREAS.ADD_TO_WISHLIST,
            },
          })
        }}
        additionalContent={
          hasSessions ? (
            <StyledLink
              width="initial"
              size={BUTTON_SIZE.L}
              href={`${ROUTE.FIND_AN_EXPERT_CONSULTATION_TAB_DYNAMIC({
                slug,
              })}#${EXPERT_BOOK_CONSULTATION_ID}`}
            >
              Book a consultation
            </StyledLink>
          ) : null
        }
        order={{ MOBILE: 2, TABLET: 1 }}
        width={{ MOBILE: '100%', TABLET: '35%' }}
      />
      {hasExpertCarousel && (
        <Flex
          order={{ MOBILE: 1, TABLET: 2 }}
          width={{ MOBILE: '100%', TABLET: '65%' }}
        >
          <ExpertCarousel images={images} />
        </Flex>
      )}
    </Flex>
  )
}
